/**
 * notePrefill - the sessionStorage handoff seam between the email
 * drafter door ("Log as case note") and the NoteComposer (Kith note
 * pipeline).
 *
 * The SPICE doors use the same seam (founder ruling: SPICE notes follow
 * the exact same path as care-management notes): "Log as session note"
 * on the SPICE email and the decline form write a prefill with
 * service 'spice' and open the composer the same way.
 *
 * Shape of the flow (founder direction: inbox coordination becomes a
 * documented, billed record entry):
 *   1. The CM clicks "Log as case note" on an email draft and picks the
 *      client (drafts carry no client_id; the recipient is free text).
 *   2. The door writes a NotePrefill here and navigates to
 *      /t/{slug}/clients/{clientId}?tab=notes.
 *   3. NoteComposer peeks the prefill on mount, seeds the narrative +
 *      hours + occurred date, then clears the key. NOTHING auto-posts:
 *      the CM reviews and posts, and the normal pipeline (extraction,
 *      confirm cards, sufficiency gate) runs on the posted note.
 *
 * Why sessionStorage (not state or localStorage): the handoff crosses a
 * route navigation (drafter surface to client record), so component
 * state cannot carry it; sessionStorage is tab-scoped and dies with the
 * tab, which is the right hygiene for narrative text on a shared
 * workstation (tighter than the composer's own 24h localStorage draft).
 * A short TTL backstops the case where the record page never mounts the
 * composer (e.g. the Notes tab is hidden for the role).
 *
 * Tenant-neutral: keys carry the tenant slug; no Peace-specific data.
 */

/** Where the prefill came from: an email draft (a care manager's "Log as
 *  case note", or the SPICE email's "Log as session note"), or the SPICE
 *  decline form. The composer's hint names it. */
export type NotePrefillSource = 'email_draft' | 'spice_decline';

export interface NotePrefill {
  narrative: string;
  /** Suggested billable hours (from the draft's billing suggestion). */
  hours?: number;
  /** YYYY-MM-DD; the draft's date. Composer defaults to today if absent. */
  occurred_at?: string;
  source: NotePrefillSource;
  /** 'spice' from a SPICE door: the composer starts on a SPICE band (the
   *  client's own session kind) and the narrative leads the box, so the
   *  note's first line stays the builder's stamp (the SPICE tab reads a
   *  session's kind from it). Absent for a care manager's door. */
  service?: 'spice';
  saved_at: number;
}

/** Unconsumed prefills die after 15 minutes (navigation is immediate;
 *  anything older is a stranded handoff, not an intent). */
export const NOTE_PREFILL_TTL_MS = 15 * 60 * 1000;

export function notePrefillKey(tenantSlug: string, clientId: string): string {
  return `kith-note-prefill:${tenantSlug}:${clientId}`;
}

const OCCURRED_AT_RE = /^\d{4}-\d{2}-\d{2}$/;

export function writeNotePrefill(
  tenantSlug: string,
  clientId: string,
  prefill: Omit<NotePrefill, 'saved_at'>,
): void {
  try {
    const stored: NotePrefill = { ...prefill, saved_at: Date.now() };
    window.sessionStorage.setItem(notePrefillKey(tenantSlug, clientId), JSON.stringify(stored));
  } catch {
    // sessionStorage unavailable (private mode / quota) - the door still
    // navigates; the composer just opens empty.
  }
}

/**
 * Read the prefill WITHOUT clearing it. Validates shape + TTL; anything
 * unrecognized or expired is removed and reads as null. Peek + a separate
 * clear (instead of read-and-clear in one call) keeps the composer's
 * mount-time consumption idempotent under React double-render.
 */
export function peekNotePrefill(tenantSlug: string, clientId: string): NotePrefill | null {
  const key = notePrefillKey(tenantSlug, clientId);
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      typeof (parsed as NotePrefill).narrative === 'string' &&
      (parsed as NotePrefill).narrative.length > 0 &&
      ((parsed as NotePrefill).source === 'email_draft' ||
        (parsed as NotePrefill).source === 'spice_decline') &&
      typeof (parsed as NotePrefill).saved_at === 'number' &&
      Date.now() - (parsed as NotePrefill).saved_at <= NOTE_PREFILL_TTL_MS
    ) {
      const p = parsed as NotePrefill;
      return {
        narrative: p.narrative,
        hours: typeof p.hours === 'number' && Number.isFinite(p.hours) && p.hours >= 0 ? p.hours : undefined,
        occurred_at:
          typeof p.occurred_at === 'string' && OCCURRED_AT_RE.test(p.occurred_at)
            ? p.occurred_at
            : undefined,
        source: p.source,
        ...(p.service === 'spice' ? { service: 'spice' as const } : {}),
        saved_at: p.saved_at,
      };
    }
    window.sessionStorage.removeItem(key);
    return null;
  } catch {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
    return null;
  }
}

export function clearNotePrefill(tenantSlug: string, clientId: string): void {
  try {
    window.sessionStorage.removeItem(notePrefillKey(tenantSlug, clientId));
  } catch {
    // ignore
  }
}
